import React, { useState, useEffect } from 'react';
import { Shield, Check, X, Info } from 'lucide-react';

export default function CookieConsent() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('samtykke'); // 'samtykke' | 'detaljer' | 'om'
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true
    preferences: true,
    statistics: true,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // For a clean first-time load that doesn't block testing, we can show a small toast or badge
      // We keep it closed by default, but users can open it via the badge or footer
    } else {
      try {
        setPreferences(JSON.parse(consent));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      preferences: true,
      statistics: true,
      marketing: true
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setIsOpen(false);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    setIsOpen(false);
  };

  const handleDenyAll = () => {
    const allDenied = {
      necessary: true,
      preferences: false,
      statistics: false,
      marketing: false
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allDenied));
    setPreferences(allDenied);
    setIsOpen(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      {/* Small Floating Cookie Badge (Bottom-Left) */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-40 bg-slate-900 hover:bg-sky-500 text-white p-3 shadow-lg hover:shadow-sky-500/20 transition-all border border-slate-800 flex items-center gap-2 group cursor-pointer"
        aria-label="Cookie-indstillinger"
        title="Cookie-indstillinger"
      >
        <Shield className="w-5 h-5 text-sky-400 group-hover:text-white transition-colors" />
        <span className="text-xs font-bold uppercase tracking-wider hidden sm:inline-block max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300">
          Cookies
        </span>
      </button>

      {/* Cookie Consent Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs">
          <div 
            className="w-full max-w-2xl bg-white text-slate-900 shadow-2xl rounded-none border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            
            {/* Header */}
            <div className="p-6 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl font-extrabold tracking-tight">
                  PETER <span className="text-sky-500">SLOTH</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                  aria-label="Luk"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Tabs */}
              <div className="flex border-b border-slate-200">
                <button
                  onClick={() => setActiveTab('samtykke')}
                  className={`py-2 px-4 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'samtykke'
                      ? 'border-sky-500 text-sky-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Samtykke
                </button>
                <button
                  onClick={() => setActiveTab('detaljer')}
                  className={`py-2 px-4 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'detaljer'
                      ? 'border-sky-500 text-sky-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Detaljer
                </button>
                <button
                  onClick={() => setActiveTab('om')}
                  className={`py-2 px-4 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
                    activeTab === 'om'
                      ? 'border-sky-500 text-sky-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  Om cookies
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto flex-1 text-sm text-slate-600 leading-relaxed space-y-4">
              {activeTab === 'samtykke' && (
                <>
                  <p className="font-semibold text-slate-800">
                    Vi bruger cookies til at tilpasse vores indhold og annoncer, til at vise dig funktioner til sociale medier og til at analysere vores trafik.
                  </p>
                  <p>
                    Vi deler også oplysninger om din brug af vores website med vores partnere inden for sociale medier, annonceringspartnere og analysepartnere. Vores partnere kan kombinere disse data med andre oplysninger, du har givet dem, eller som de har indsamlet fra din brug af deres tjenester.
                  </p>
                  <p>
                    Ved at klikke på "Accepter alle", giver du samtykke til alle disse formål. Du kan også vælge at specificere dine præferencer nedenfor.
                  </p>
                </>
              )}

              {activeTab === 'detaljer' && (
                <div className="space-y-4">
                  <div className="border-b border-slate-100 pb-3">
                    <h4 className="font-bold text-slate-800 uppercase text-xs tracking-wider">Nødvendige (Altid aktive)</h4>
                    <p className="text-xs text-slate-500 mt-1">Nødvendige cookies hjælper med at gøre en hjemmeside brugbar ved at aktivere grundlæggende funktioner såsom sidenavigation og adgang til sikre områder af hjemmesiden.</p>
                  </div>
                  <div className="border-b border-slate-100 pb-3">
                    <h4 className="font-bold text-slate-800 uppercase text-xs tracking-wider">Præferencer</h4>
                    <p className="text-xs text-slate-500 mt-1">Præference-cookies gør det muligt for en hjemmeside at huske oplysninger, der ændrer den måde, hjemmesiden opfører sig eller ser ud på, f.eks. dit foretrukne sprog.</p>
                  </div>
                  <div className="border-b border-slate-100 pb-3">
                    <h4 className="font-bold text-slate-800 uppercase text-xs tracking-wider">Statistik</h4>
                    <p className="text-xs text-slate-500 mt-1">Statistiske cookies hjælper webstedsejere med at forstå, hvordan besøgende interagerer med hjemmesider by at indsamle og rapportere oplysninger anonymt.</p>
                  </div>
                  <div className="border-b border-slate-100 pb-3">
                    <h4 className="font-bold text-slate-800 uppercase text-xs tracking-wider">Marketing</h4>
                    <p className="text-xs text-slate-500 mt-1">Marketing-cookies bruges til at spore besøgende på tværs af hjemmesider. Hensigten er at vise annoncer, der er relevante og engagerende for den enkelte bruger.</p>
                  </div>
                </div>
              )}

              {activeTab === 'om' && (
                <>
                  <p>
                    Cookies er små tekstfiler, som kan bruges af websteder til at gøre en brugers oplevelse mere effektiv.
                  </p>
                  <p>
                    Loven angiver, at vi kan gemme cookies på din enhed, hvis de er strengt nødvendige for at sikre leveringen af den tjeneste, du udtrykkeligt har anmodet om at bruge. For alle andre typer cookies skal vi indhente dit samtykke.
                  </p>
                  <p>
                    Dette websted bruger forskellige typer af cookies. Nogle cookies sættes af tredjepartstjenester, der vises på vores sider.
                  </p>
                </>
              )}
            </div>

            {/* Toggle Switch Grid */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="flex items-center justify-between p-2 bg-white border border-slate-200">
                <span className="text-xs font-bold uppercase text-slate-700">Nødvendig</span>
                <div className="w-8 h-5 bg-sky-500 rounded-full flex items-center justify-end p-0.5 cursor-not-allowed opacity-80">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>
              
              <button
                onClick={() => togglePreference('preferences')}
                className="flex items-center justify-between p-2 bg-white border border-slate-200 hover:border-slate-300 transition-all text-left cursor-pointer"
              >
                <span className="text-xs font-bold uppercase text-slate-700">Præf.</span>
                <div className={`w-8 h-5 rounded-full flex items-center p-0.5 transition-all ${preferences.preferences ? 'bg-sky-500 justify-end' : 'bg-slate-300 justify-start'}`}>
                  <div className="w-4 h-4 bg-white rounded-full shadow-xs" />
                </div>
              </button>

              <button
                onClick={() => togglePreference('statistics')}
                className="flex items-center justify-between p-2 bg-white border border-slate-200 hover:border-slate-300 transition-all text-left cursor-pointer"
              >
                <span className="text-xs font-bold uppercase text-slate-700">Statistik</span>
                <div className={`w-8 h-5 rounded-full flex items-center p-0.5 transition-all ${preferences.statistics ? 'bg-sky-500 justify-end' : 'bg-slate-300 justify-start'}`}>
                  <div className="w-4 h-4 bg-white rounded-full shadow-xs" />
                </div>
              </button>

              <button
                onClick={() => togglePreference('marketing')}
                className="flex items-center justify-between p-2 bg-white border border-slate-200 hover:border-slate-300 transition-all text-left cursor-pointer"
              >
                <span className="text-xs font-bold uppercase text-slate-700">Marketing</span>
                <div className={`w-8 h-5 rounded-full flex items-center p-0.5 transition-all ${preferences.marketing ? 'bg-sky-500 justify-end' : 'bg-slate-300 justify-start'}`}>
                  <div className="w-4 h-4 bg-white rounded-full shadow-xs" />
                </div>
              </button>
            </div>

            {/* Footer Actions */}
            <div className="p-6 border-t border-slate-200 bg-slate-100 flex flex-col sm:flex-row justify-between gap-3">
              <button
                onClick={handleDenyAll}
                className="w-full sm:w-auto px-6 py-3 border border-slate-300 text-slate-700 font-bold uppercase text-xs tracking-wider hover:bg-slate-200 hover:text-slate-900 transition-all rounded-none text-center cursor-pointer"
              >
                Afvis alle
              </button>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={handleAcceptSelected}
                  className="px-6 py-3 border border-slate-300 text-slate-700 font-bold uppercase text-xs tracking-wider hover:bg-slate-200 hover:text-slate-900 transition-all rounded-none text-center cursor-pointer"
                >
                  Tillad valgte
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase text-xs tracking-wider transition-all rounded-none text-center cursor-pointer"
                >
                  Accepter alle
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
}
